import * as React from 'react'; // eslint-disable-line
import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { COLOR, FONT_SIZE, FONT_STACK, SPACING } from '../../../../packages/constants';
import { LoadingSpinner, Icon } from '../../../loading-spinner';
import { AccountCell, AccountCellProps } from '../account-cell/account-cell';
import { StyledEdit } from '../icons/icons';

export interface AccountCellInputProps extends AccountCellInputDivProps {
    cellId: string;
    inputProps: InputProps;
}

interface AccountCellInputDivProps extends AccountCellProps {
    isSaving?: boolean;
}

interface InputProps extends React.HTMLProps<HTMLInputElement> {
    dataId: string;
    saveFn: SaveCallbackFn;
}

export type SaveCallbackFn = ({ cellId, value }: { cellId: string; value: string }) => Promise<SaveCallbackFnResponse>;

export interface SaveCallbackFnResponse {
    data: {
        value: string | number;
    };
}

const StyledLoadingSpinner = styled(LoadingSpinner)`
    position: absolute;
    bottom: 5px;
    right: 2px;

    ${Icon} {
        margin: 0;
    }
`;

const AccountCellInputDiv = styled(AccountCell)<AccountCellInputDivProps>``;

const InvisibleInput = styled.input`
    border: 1px solid transparent;
    background-color: transparent;
    font-size: ${FONT_SIZE.X_SMALL};
    font-family: ${FONT_STACK};
    padding: ${SPACING.X_SMALL} ${SPACING.SMALL};
    margin: 0;
    width: 140px;
    z-index: 1;
    color: ${COLOR.DATAGRID.DATAGRID_TEXT};

    &:hover + ${StyledEdit} {
        fill: ${COLOR.BRAND.REPORTING};
    }

    &:focus {
        box-shadow: 0 0 3px inset ${COLOR.BRAND.REPORTING};

        + ${StyledEdit} {
            fill: ${COLOR.BRAND.REPORTING};
        }
    }
`;

const updateCell = ({
    e,
    value,
    setCellValue,
    setLastSavedValue,
    lastModifiedRef,
}: {
    e: React.BaseSyntheticEvent;
    value: string | number;
    setCellValue: React.Dispatch<React.SetStateAction<string | number | string[]>>;
    setLastSavedValue: React.Dispatch<React.SetStateAction<string | number | string[]>>;
    lastModifiedRef: React.MutableRefObject<number>;
}) => {
    /**
     * Before updating the cell, ensure the user is not currently editing it
     * by comparing when it was lastModifed to when the event fired.
     * This is necessary to prevent overwriting subsequent changes the user made
     * while the callback was beind handled.
     * It's important that lastModified is a ref value so it is live rather than
     * when the function was called.
     */
    if (lastModifiedRef.current < e.timeStamp) {
        setCellValue(value); // write to the cell the new value
    }
    // always update the lastSavedValue
    setLastSavedValue(value);
};

/**
 * Handles saving and updating the cell saving state and value accordingly
 * Will save if it is passed an unsaved value
 * Calls a saveCallbackFn passed in to it so the value can be persisted.
 */
const saveHandler = async ({
    e,
    cellId,
    lastSavedValue,
    lastModifiedRef,
    setCellValue,
    setLastAttemptedSaveValue,
    setLastSavedValue,
    saveCallbackFn,
    isMountedRef,
}: {
    e: React.BaseSyntheticEvent;
    cellId: string;
    lastSavedValue: string | number | string[];
    lastModifiedRef: React.MutableRefObject<number>;
    setCellValue: React.Dispatch<React.SetStateAction<string | number | string[]>>;
    setLastAttemptedSaveValue: React.Dispatch<React.SetStateAction<string | number | string[]>>;
    setLastSavedValue: React.Dispatch<React.SetStateAction<string | number | string[]>>;
    saveCallbackFn: SaveCallbackFn;
    isMountedRef: React.MutableRefObject<boolean>;
}) => {
    e.persist(); // react 16 uses event pooling https://reactjs.org/docs/legacy-event-pooling.html
    const value = e.target.value;
    setLastAttemptedSaveValue(value);
    // only save if the value has changed from when it was last saved
    if (lastSavedValue !== value) {
        try {
            const { data } = await saveCallbackFn({ cellId, value });
            if (!isMountedRef.current) {
                return;
            }
            // write the value back to the cell to ensure consistency from the server to client
            updateCell({ e, value: data.value, setCellValue, setLastSavedValue, lastModifiedRef });
        } catch {
            console.error('Failed to save', cellId, value);
        }
    }
};

const keyHandler = ({
    e,
    saveFn,
    restoreFn,
}: {
    e: React.KeyboardEvent<HTMLInputElement>;
    saveFn: () => Promise<void>;
    restoreFn: () => void;
}) => {
    if (e.key === 'Enter') {
        saveFn();
    } else if (e.key === 'Escape' || e.key === 'Esc') {
        restoreFn();
    }
};

export const AccountCellInput = ({
    cellId,
    inputProps,
    hasTextAlignedLeft,
    isGroup,
    canBeToggled,
    isDisplayed,
    hasNoPadding,
}: AccountCellInputProps) => {
    const [cellValue, setCellValue] = useState(
        inputProps.value === null || typeof inputProps.value === 'undefined' ? '' : inputProps.value,
    ); // the current value
    const [lastAttemptedSaveValue, setLastAttemptedSaveValue] = useState(
        inputProps.value === null || typeof inputProps.value === 'undefined' ? '' : inputProps.value,
    );
    const [lastSavedValue, setLastSavedValue] = useState(
        inputProps.value === null || typeof inputProps.value === 'undefined' ? '' : inputProps.value,
    ); // so we can compare or restore the last saved value
    const [savingState, setSavingState] = useState({ value: false, lastAttemptedSaveDate: 0 }); // just for display purposes, may have actually completed saving as it has a minimum duration

    const isSaving = savingState.value;
    const lastModifiedRef = useRef(0);
    const savingTimerRef = useRef(0);
    const isMountedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    /**
     * if we get passed in a new value, we should use that value
     */
    useEffect(() => {
        setCellValue(inputProps.value || '');
    }, [inputProps.value]);

    /**
     * ensure that given the value has not changed (saving the most recent value), saving state isn't set false for a min duration
     * if the value has changed (there's a new save pending), the saving state can be cleared
     */
    useEffect(() => {
        if (!isMountedRef.current) {
            return;
        }

        const currentValueMatchesAttemptedSave = cellValue === lastAttemptedSaveValue;
        const attemptedSaveValueMatchesSaved = lastAttemptedSaveValue === lastSavedValue;
        const showAsSaving =
            currentValueMatchesAttemptedSave && lastAttemptedSaveValue !== null && !attemptedSaveValueMatchesSaved;
        const now = Date.now();
        const lastAttemptedSaveDate =
            lastAttemptedSaveValue !== null && !attemptedSaveValueMatchesSaved
                ? now
                : savingState.lastAttemptedSaveDate; // the date the user last tried to save a value different to the currently saved value
        const diff = now - lastAttemptedSaveDate; // how long it has been since user last tried to save
        const minDuration = 500; // minimum time to show saving state for

        // when we're saving the last entered value and clearing the save state a minimum duration must have passed
        if (cellValue === lastAttemptedSaveValue && showAsSaving === false) {
            savingTimerRef.current = window.setTimeout(() => {
                setSavingState({ value: showAsSaving, lastAttemptedSaveDate });
            }, Math.max(0, minDuration - diff));
        } else {
            setSavingState({ value: showAsSaving, lastAttemptedSaveDate });
        }
        // cleanup
        return () => {
            window.clearTimeout(savingTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellValue, lastAttemptedSaveValue, lastSavedValue]);

    return (
        <AccountCellInputDiv
            isSaving={isSaving}
            hasTextAlignedLeft={hasTextAlignedLeft}
            isGroup={isGroup}
            canBeToggled={canBeToggled}
            isDisplayed={isDisplayed}
            hasNoPadding={hasNoPadding}
        >
            <InvisibleInput
                type={inputProps.type}
                value={cellValue}
                onChange={(e) => {
                    setCellValue(e.currentTarget.value);
                    lastModifiedRef.current = e.timeStamp;
                }}
                onBlur={(e) =>
                    saveHandler({
                        e,
                        cellId,
                        // TODO: fix issue #292
                        // @ts-ignore
                        lastSavedValue,
                        lastModifiedRef,
                        // TODO: fix issue #292
                        // @ts-ignore
                        setCellValue,
                        // TODO: fix issue #292
                        // @ts-ignore
                        setLastAttemptedSaveValue,
                        // TODO: fix issue #292
                        // @ts-ignore
                        setLastSavedValue,
                        saveCallbackFn: inputProps.saveFn,
                        isMountedRef,
                    })
                }
                onKeyDown={(e) =>
                    keyHandler({
                        e,
                        // TODO: fix issue #292
                        // @ts-ignore
                        saveFn: saveHandler.bind(e, {
                            e,
                            cellId,
                            lastSavedValue,
                            lastModifiedRef,
                            setCellValue,
                            setLastAttemptedSaveValue,
                            setLastSavedValue,
                            saveCallbackFn: inputProps.saveFn,
                            isMountedRef,
                        }),
                        restoreFn: setCellValue.bind(e, lastAttemptedSaveValue === null ? '' : lastAttemptedSaveValue),
                    })
                }
                data-test-id={inputProps.dataId}
            />
            {!isSaving && <StyledEdit />}
            {isSaving && <StyledLoadingSpinner color="black" iconWidth={'12px'} iconHeight={'12px'} />}
        </AccountCellInputDiv>
    );
};
